import React, { useEffect, useState } from "react";
import Modal from "../Modal/Modal";
import CloseIcon from "../../assets/svg/close-icon";
import Button from "../Button/Button";
import CustomEmailEditor from "../ComposeEmailEditor/ComposeEmailEditor";
import EmailToolbar from "../ComposeEmailEditor/components/EmailToolbar";
import { Editor } from "@tiptap/react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { customToast } from "../../utils/custom-toast/custom-toast";
import {
  useLazyGetEmailSignatureQuery,
  useUpdateEmailSignatureMutation,
} from "../../redux/services/authApi";
import { sanitizeEmailSignature } from "../../utils/functions/email-sanitizer";
import DoNotDisturbIcon from "../../assets/svg/do-not-disturb-icon";
import { updateClient } from "../../redux/slices/authSlice";

const ToolbarSchema = {
  history: true,
  typography: {
    fontFamily: true,
    fontSize: true,
  },
  textFormatting: {
    bold: true,
    italic: true,
    underline: true,
    strikethrough: false,
  },
  textStyling: {
    textColor: true,
  },
  contentStructure: {
    lists: true,
    alignment: true,
    indentation: false,
    blockquotes: false,
  },
  media: {
    links: true,
    images: false,
    attachments: false,
  },
  interactive: {
    emojis: false,
  },
  utilities: {
    clearFormatting: false,
  },
};

// const templates_data = [
//   {
//     id: "1",
//     name: "Test Signature 2",
//   },
//   {
//     id: "2",
//     name: "Vita Student - London",
//   },
//   {
//     id: "3",
//     name: "Test Signature 3",
//   },
//   {
//     id: "4",
//     name: "Test Signature 4",
//   },
//   {
//     id: "5",
//     name: "Test Signature 5",
//   },
//   {
//     id: "6",
//     name: "Test Signature 6",
//   },
//   {
//     id: "7",
//     name: "Test Signature 7",
//   },
//   {
//     id: "8",
//     name: "Test Signature 8",
//   },
//   {
//     id: "9",
//     name: "Test Signature 9",
//   },
//   {
//     id: "10",
//     name: "Test Signature 10",
//   },
//   {
//     id: "11",
//     name: "Test Signature 11",
//   },
//   {
//     id: "12",
//     name: "Test Signature 12",
//   },
//   {
//     id: "13",
//     name: "Test Signature 13",
//   },
//   {
//     id: "14",
//     name: "Test Signature 14",
//   },
// ];

const hasComplexHtml = (html: string): boolean => {
  if (!html) return false;
  return /<table[\s>]/i.test(html) || /\sstyle\s*=/i.test(html);
};

const EmailSignatureModal = ({
  open,
  onOpenChange,
  onOpenDarkModePreview,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenDarkModePreview: (html: string) => void;
  isEmailSignatureDarkModePreviewOpen: boolean;
}) => {
  const [message, setMessage] = useState("");
  const [editor, setEditor] = useState<Editor | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  const dispatch = useDispatch();
  const config = useSelector((state: RootState) => state.auth.client);
  const [updateEmailSignature] = useUpdateEmailSignatureMutation();
  const [fetchEmailSignature] = useLazyGetEmailSignatureQuery();

  useEffect(() => {
    if (!open) {
      setMessage(config?.email_signature?.html_content || "");
      setEditor(null);
      setIsLoading(false);
      setIsHtmlMode(false);
      return;
    }

    if (config?.email_signature?.html_content) {
      setMessage(config.email_signature.html_content);
      setIsHtmlMode(hasComplexHtml(config.email_signature.html_content));
    } else {
      fetchEmailSignature()
        .unwrap()
        .then((res) => {
          if (res?.email_signature) {
            setMessage(res.email_signature);
            setIsHtmlMode(hasComplexHtml(res.email_signature));
            dispatch(
              updateClient({ email_signature: { html_content: res.email_signature } })
            );
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleEditorReady = (editorInstance: Editor) => {
    setEditor(editorInstance);
  };

  const handleSave = async () => {
    if (!message?.trim()) {
      customToast("Please enter an email signature", "error");
      return;
    }

    setIsLoading(true);
    try {
      const sanitizedSignature = sanitizeEmailSignature(message);

      const response = await updateEmailSignature({
        html_content: sanitizedSignature,
      }).unwrap();
      setMessage(response.email_signature);

      dispatch(
        updateClient({
          email_signature: {
            html_content: response.email_signature,
          },
        })
      );

      customToast("Email signature saved successfully", "success");
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving email signature:", error);
      customToast("Failed to save email signature. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      showHeader={false}
      contentClassName="w-[800px] h-[500px] overflow-x-hidden overflow-y-hidden bg-fixed-white dark:bg-fixed-white-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk"
      overlayClassName="!bg-black/80"
      bodyClassName="!p-0 w-full h-full"
      preventOutsideClick={true}
      closeOnEscape={false}
    >
      <div className="flex flex-col gap-[16px] w-full h-full px-[18px] py-[12px]">
        <div className="w-full flex flex-col ">
          <div className="w-full h-full flex justify-between">
            <div>
              <h2 className="text-base font-semibold text-heading-text dark:text-heading-text">
                Email Signature
              </h2>
              <p className="text-xs font-normal text-dark-gray-fixed dark:text-dark-gray-fixed-dk">
                Set up your signature below.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className={`w-[15px] h-[15px] bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110`}
              aria-label="Close modal"
            >
              <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
            </button>
          </div>

          <div className="flex items-center gap-[4px] mt-[8px]">
            <button
              type="button"
              onClick={() => setIsHtmlMode(false)}
              className={`px-[10px] py-[4px] rounded-[6px] text-xs font-medium transition-colors duration-200 ${
                !isHtmlMode
                  ? "bg-verba-indigo-fixed dark:bg-verba-indigo-fixed-dk text-fixed-white dark:text-fixed-white-dk"
                  : "bg-transparent text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk"
              }`}
            >
              Visual
            </button>
            <button
              type="button"
              onClick={() => setIsHtmlMode(true)}
              className={`px-[10px] py-[4px] rounded-[6px] text-xs font-medium transition-colors duration-200 ${
                isHtmlMode
                  ? "bg-verba-indigo-fixed dark:bg-verba-indigo-fixed-dk text-fixed-white dark:text-fixed-white-dk"
                  : "bg-transparent text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk border border-border-gray-fixed dark:border-border-gray-fixed-dk"
              }`}
            >
              HTML
            </button>
          </div>
        </div>

        <div className="flex w-full h-full overflow-hidden">
          {/* <div className="w-[220px] shrink-0 overflow-y-auto no-scrollbar border-r border-border-gray-fixed dark:border-border-gray-fixed-dk flex flex-col">
            {templates_data && templates_data?.length > 0 && (
              <div>
                {templates_data?.map((template) => (
                  <div
                    key={template.id}
                    className="cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk flex flex-col w-full px-[12px] py-[10px] border-b border-border-gray-fixed dark:border-border-gray-fixed-dk"
                  >
                    <span className="text-md font-normal text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk">
                      {template.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div> */}

          <div className="flex-1 flex flex-col overflow-hidden">
            {isHtmlMode ? (
              <div className="flex flex-col flex-1 gap-[8px] overflow-hidden">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Paste your HTML signature here..."
                  className="flex-1 min-h-0 w-full px-[12px] py-[10px] rounded-[8px] border border-border-gray-fixed dark:border-border-gray-fixed-dk bg-fixed-white dark:bg-fixed-white-dk text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk text-xs font-mono resize-none focus:outline-none"
                  spellCheck={false}
                />
                {message?.trim() && (
                  <div className="flex-1 min-h-0 overflow-y-auto rounded-[8px] border border-border-gray-fixed dark:border-border-gray-fixed-dk px-[12px] py-[10px]">
                    <p className="text-[10px] font-medium text-dark-gray-fixed dark:text-dark-gray-fixed-dk mb-[6px] uppercase tracking-wider">
                      Preview
                    </p>
                    <div
                      className="text-sm text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk"
                      dangerouslySetInnerHTML={{ __html: message }}
                    />
                  </div>
                )}
              </div>
            ) : hasComplexHtml(message) ? (
              <div className="px-[12px] py-[10px] rounded-[8px] flex-1 overflow-y-auto border border-border-gray-fixed dark:border-border-gray-fixed-dk">
                <div
                  className="text-sm text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk"
                  dangerouslySetInnerHTML={{ __html: message }}
                />
              </div>
            ) : (
              <>
                <div className="px-[12px] rounded-[8px] flex-1 overflow-y-auto border border-border-gray-fixed dark:border-border-gray-fixed-dk">
                  <CustomEmailEditor
                    message={message}
                    onMessageChange={setMessage}
                    onEditorReady={handleEditorReady}
                    placeholder="Enter your email signature here..."
                  />
                </div>

                {editor && (
                  <div className="shrink-0 py-[8px]">
                    <EmailToolbar
                      editor={editor}
                      schema={ToolbarSchema}
                      toolbarExtraClass="w-full max-w-fit !ml-0 !my-0 shadow-none"
                    />
                  </div>
                )}
              </>
            )}

            <div className="shrink-0 flex justify-between gap-[8px] mt-[24px]">
              <Button
                type="button"
                innerText="Dark Mode Preview"
                Icon={DoNotDisturbIcon}
                iconLeft={true}
                iconClass="w-[14px] h-[14px] text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk"
                innerTextClass="shrink-0 text-md font-medium text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk"
                buttonContainerClass="w-[125px] h-[18px]"
                onClick={() => onOpenDarkModePreview(message)}
              />
              <div className="flex gap-[8px]">
                <Button
                  type="button"
                  innerText="Cancel"
                  onClick={handleCancel}
                  disabled={isLoading}
                  buttonContainerClass="rounded-[6px] flex items-center justify-center w-[100px] h-[26px] bg-fixed-white dark:bg-fixed-white-dk border border-verba-indigo-fixed dark:border-verba-indigo-fixed-dk"
                  innerTextClass="shrink-0 text-md font-semibold text-verba-indigo-fixed dark:text-verba-indigo-fixed-dk"
                />
                <Button
                  type="button"
                  innerText={"Save Signature"}
                  onClick={handleSave}
                  isLoading={isLoading}
                  disabled={isLoading || !message?.trim()}
                  buttonContainerClass="rounded-[6px] flex items-center justify-center w-[135px] h-[26px] bg-verba-indigo-fixed dark:bg-verba-indigo-fixed-dk"
                  innerTextClass="shrink-0 text-md font-semibold text-fixed-white dark:text-fixed-white-dk"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EmailSignatureModal;
